/**
 * 基准轴可视对象。
 * 职责：
 * 1. 直接以 `THREE.Line` 承载基准轴显示；
 * 2. 标签对象独立创建，由管理器统一挂载到专用标签组；
 * 3. 不承载求解逻辑，仅负责最终可视化表达。
 */

import * as THREE from 'three/webgpu';
import {CSS2DObject} from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import {syncThinChainLineGeometry, ThinChainLIneNodeMaterial,} from '../material';
import {addObject3DToParent} from '../utils/scene-object/addObject3DToParent';
import {ReferenceCreationContext} from './ReferenceCreationContext';


/** 基准轴稳定业务 ID 计数器。 */
let datumAxisIdCount = 0;


/**
 * 格式化基准轴调试向量。
 *
 * @param value 向量值。
 * @returns 便于控制台查看的普通对象。
 */
function formatDatumAxisDebugVector(value: THREE.Vector3): { x: number; y: number; z: number } {
  return {
    x: value.x,
    y: value.y,
    z: value.z,
  };
}

/**
 * 读取 position 属性中的两个端点，便于调试 BufferGeometry 写回结果。
 *
 * @param axis 基准轴对象。
 * @returns position 调试快照。
 */
function buildDatumAxisPositionDebug(axis: DatumAxis): Record<string, number> | null {
  const positionAttribute = axis.geometry.getAttribute('position');
  if (!(positionAttribute instanceof THREE.BufferAttribute) || positionAttribute.count < 2) {
    return null;
  }
  return {
    0: positionAttribute.getX(0),
    1: positionAttribute.getY(0),
    2: positionAttribute.getZ(0),
    3: positionAttribute.getX(1),
    4: positionAttribute.getY(1),
    5: positionAttribute.getZ(1),
  };
}

/**
 * 输出基准轴正式对象调试信息。
 *
 * @param message 调试消息。
 * @param payload 调试载荷。
 */
function logDatumAxisDebug(message: string, payload: Record<string, unknown>): void {
  console.log(`[基准轴正式对象调试] ${message}`, payload);
}

/**
 * 判断对象是否为正式基准轴。
 *
 * @param value 待判断对象。
 * @returns 是否为正式基准轴对象。
 */
export function isDatumAxis(value: unknown): value is DatumAxis {
  return Reflect.get(value as object, 'isDatumAxis') === true;
}


/**
 * 读取对象所属的正式基准轴稳定 ID。
 *
 * @param value 待读取对象。
 * @returns 基准轴稳定 ID；对象不属于正式基准轴时返回 `null`。
 */
export function getDatumAxisIdFromObject(value: THREE.Object3D): number | null {
  const axisId = Reflect.get(value, 'datumAxisId');
  return Number.isFinite(axisId) ? Number(axisId) : null;
}


/**
 * 基准轴构造参数。
 */
export interface DatumAxisOptions {

  /** 基准轴稳定业务 ID；仅历史恢复时传入。 */
  id?: number;

  /** 创建来源上下文。 */
  creationContext: ReferenceCreationContext;
  /** 基准轴名称。 */
  name: string;
  /** 轴线原点。 */
  originWorld: THREE.Vector3;
  /** 轴线方向。 */
  direction: THREE.Vector3;
  /** 显示线段起点。 */
  startWorld: THREE.Vector3;
  /** 显示线段终点。 */
  endWorld: THREE.Vector3;
  /** 颜色。 */
  color?: number;
  /** 是否可见。 */
  visible?: boolean;
  /** 不透明度。 */
  opacity?: number;
  /** 用户手动创建时间戳（毫秒），用于设计树时间线排序与历史恢复。 */
  createdAt?: number;
}


/**
 * 基准轴显示状态快照。
 */
export interface DatumAxisDisplaySnapshot {
  /** 显示颜色。 */
  color: number;
  /** 不透明度。 */
  opacity: number;
}


/** 默认显示配置。 */
const DEFAULT_AXIS_DISPLAY: Required<Pick<DatumAxisOptions, 'color' | 'visible' | 'opacity'>> = {
  color: 0x4a9eff,
  visible: true,
  opacity: 0.95,
};


/** 基准轴点画线材质名称。 */
const DATUM_AXIS_DASH_DOT_MATERIAL_NAME = '基准轴-点画线材质';
/** 基准轴点画线长线段屏幕节奏。 */
const DATUM_AXIS_DASH_SIZE = 22;
/** 基准轴点画线长线段与点之间的间隔。 */
const DATUM_AXIS_GAP_SIZE = 4;
/** 基准轴点画线单点尺寸。 */
const DATUM_AXIS_DOT_SIZE = 4;
/** 基准轴点画线线宽。 */
const DATUM_AXIS_LINE_WIDTH = 1;

/**
 * 创建基准轴点画线材质。
 *
 * 设计说明：
 * 1. 正式基准轴应按 SolidWorks 中心线语义显示为点画线；
 * 2. 这里直接复用材质目录中的细点画线 Node 材质，避免基准轴自行实现线型逻辑；
 * 3. 每条基准轴独立持有材质实例，因为点画线材质需要保存本线段的点列。
 *
 * @param color 显示颜色。
 * @param opacity 显示不透明度。
 * @returns 基准轴点画线材质。
 */
function createDatumAxisDashDotMaterial(
  color: THREE.ColorRepresentation,
  opacity: number,
): ThinChainLIneNodeMaterial {
  const material = new ThinChainLIneNodeMaterial({
    color,
    linewidth: DATUM_AXIS_LINE_WIDTH,
    dashSize: DATUM_AXIS_DASH_SIZE,
    gapSize: DATUM_AXIS_GAP_SIZE,
    dotSize: DATUM_AXIS_DOT_SIZE,
    transparent: true,
    opacity,
    depthWrite: false,
  });

  /**
   * NodeMaterial 的 name 字段在类型上不是构造参数显式字段，
   * 通过 Reflect 写入可以保持与现有材质目录命名方式一致。
   */
  Reflect.set(material, 'name', DATUM_AXIS_DASH_DOT_MATERIAL_NAME);
  return material;
}


/**
 * 基准轴。
 */
export class DatumAxis extends THREE.Line {

  /** 运行时类型标记。 */
  public readonly isDatumAxis: boolean = true;

  /** 基准轴稳定业务 ID。 */
  public readonly datumAxisId: number;


  /** 显示名称。 */
  public declare name: string;
  /** 创建来源上下文。 */
  public readonly creationContext: ReferenceCreationContext;
  /** 轴线原点。 */
  public readonly originWorld: THREE.Vector3;
  /** 轴线方向。 */
  public readonly direction: THREE.Vector3;
  /** 标签对象。 */
  private readonly labelObject: CSS2DObject;

  /** 显示颜色。 */
  private readonly displayColor: number;
  /** 显示不透明度。 */
  private readonly displayOpacity: number;


  /**
   * @param options 构造参数。
   */
  public constructor(options: DatumAxisOptions) {
    const geometry = new THREE.BufferGeometry().setFromPoints([
      options.startWorld.clone(),
      options.endWorld.clone(),
    ]);
    const resolvedColor = options.color ?? DEFAULT_AXIS_DISPLAY.color;
    const resolvedOpacity = options.opacity ?? DEFAULT_AXIS_DISPLAY.opacity;

    const material = createDatumAxisDashDotMaterial(resolvedColor, resolvedOpacity);

    super(geometry, material);

    syncThinChainLineGeometry(this);


    this.displayColor = resolvedColor;
    this.displayOpacity = resolvedOpacity;


    if (Number.isFinite(options.id)) {
      /**
       * 基准轴引用必须使用自有稳定 ID，而不能复用 Three.js Object3D.id。
       * Object3D.id 是运行时对象流水号，Redo 后无法作为业务引用真相源。
       */
      this.datumAxisId = Number(options.id);
      datumAxisIdCount = Math.max(datumAxisIdCount, this.datumAxisId + 1);
    } else {
      this.datumAxisId = datumAxisIdCount;
      datumAxisIdCount += 1;
    }
    Reflect.set(this, 'datumAxisId', this.datumAxisId);

    this.name = options.name;
    this.creationContext = options.creationContext;
    this.originWorld = options.originWorld.clone();
    this.direction = options.direction.clone().normalize();
    this.visible = options.visible ?? DEFAULT_AXIS_DISPLAY.visible;
    this.renderOrder = 992;
    this.labelObject = this.createLabel(options.startWorld, options.endWorld);
    this.labelObject.visible = this.visible;
    logDatumAxisDebug('创建正式基准轴', {
      datumAxisId: this.datumAxisId,
      creationContext: this.creationContext,
      name: this.name,
      originWorld: formatDatumAxisDebugVector(this.originWorld),
      direction: formatDatumAxisDebugVector(this.direction),
      startWorld: formatDatumAxisDebugVector(options.startWorld),
      endWorld: formatDatumAxisDebugVector(options.endWorld),
      axisLength: options.startWorld.distanceTo(options.endWorld),
      position: buildDatumAxisPositionDebug(this),
    });
  }

  /**
   * 创建标签。
   *
   * @param start 起点。
   * @param end 终点。
   * @returns 标签对象。
   */
  private createLabel(start: THREE.Vector3, end: THREE.Vector3): CSS2DObject {
    const element = document.createElement('div');
    element.className = 'datum-axis-label';
    element.textContent = this.name;
    element.style.color = '#ffffff';
    element.style.fontSize = '12px';
    element.style.fontWeight = 'bold';
    element.style.backgroundColor = 'rgba(0, 0, 0, 0.65)';
    element.style.padding = '4px 8px';
    element.style.borderRadius = '4px';
    element.style.pointerEvents = 'none';
    const label = new CSS2DObject(element);
    label.position.copy(start.clone().add(end).multiplyScalar(0.5));
    return label;
  }

  /**
   * 添加到场景。
   *
   * @param scene 场景对象。
   */
  public addToScene(scene: THREE.Scene): void {
    addObject3DToParent(this, scene);
  }

  /**
   * 从场景移除。
   *
   * @param scene 场景对象。
   */
  public removeFromScene(scene: THREE.Scene): void {
    scene.remove(this);
  }

  /**
   * 获取可视对象。
   *
   * @returns 当前对象自身。
   */
  public getObject3D(): THREE.Object3D {
    return this;
  }


  /**
   * 获取基准轴当前显示线段的世界坐标端点。
   *
   * @returns 起终点；几何缺失时返回 `null`。
   */
  public getWorldEndpoints(): LineSegmentEndpoints | null {
    const positionAttribute = this.geometry.getAttribute('position');
    if (!(positionAttribute instanceof THREE.BufferAttribute) || positionAttribute.count < 2) {
      return null;
    }

    /**
     * 正式基准轴可能在父级组下参与层级变换，因此这里统一乘以 `matrixWorld`，
     * 确保命令控制器与求解器始终读取世界坐标，而不是局部坐标。
     */
    const start = new THREE.Vector3()
      .fromBufferAttribute(positionAttribute, 0)
      .applyMatrix4(this.matrixWorld);
    const end = new THREE.Vector3()
      .fromBufferAttribute(positionAttribute, 1)
      .applyMatrix4(this.matrixWorld);

    return {
      start,
      end,
    };
  }

  /**
   * 获取基准轴当前显示线段的世界中点。
   *
   * @returns 中点；端点缺失时返回 `null`。
   */
  public getWorldMidpoint(): THREE.Vector3 | null {
    const endpoints = this.getWorldEndpoints();
    if (!endpoints) {
      return null;
    }
    return endpoints.start.clone().add(endpoints.end).multiplyScalar(0.5);
  }

  /**
   * 获取基准轴世界方向。
   *
   * @returns 单位方向向量。
   */
  public getWorldDirection(): THREE.Vector3 {
    const endpoints = this.getWorldEndpoints();
    if (endpoints) {
      const directionByEndpoints = endpoints.end.clone().sub(endpoints.start);
      if (directionByEndpoints.lengthSq() > 1e-12) {
        return directionByEndpoints.normalize();
      }
    }
    return this.direction.clone().normalize();
  }


  /**
   * 更新基准轴世界几何。
   *
   * 设计说明：
   * 1. 装配体零部件移动后，依赖其拓扑的基准轴需要更新位置与方向；
   * 2. 这里必须原地更新正式对象，不能删除重建，否则后续引用该基准轴的特征会丢失稳定 ID；
   * 3. 标签位置跟随显示线段中点，保证设计树对象与视口对象仍指向同一个 DatumAxis 实例。
   *
   * @param input
   */
  public updateFrame(input: {
    originWorld: THREE.Vector3;
    direction: THREE.Vector3;
    startWorld: THREE.Vector3;
    endWorld: THREE.Vector3;
  }): void {
    this.originWorld.copy(input.originWorld);
    this.direction.copy(input.direction).normalize();
    const positionAttribute = this.geometry.getAttribute('position');
    if (positionAttribute instanceof THREE.BufferAttribute && positionAttribute.count >= 2) {
      positionAttribute.setXYZ(0, input.startWorld.x, input.startWorld.y, input.startWorld.z);
      positionAttribute.setXYZ(1, input.endWorld.x, input.endWorld.y, input.endWorld.z);
      positionAttribute.needsUpdate = true;
    } else {
      this.geometry.setFromPoints([input.startWorld, input.endWorld]);
    }
    this.geometry.computeBoundingSphere();

    syncThinChainLineGeometry(this);

    this.labelObject.position.copy(input.startWorld).add(input.endWorld).multiplyScalar(0.5);
    this.updateMatrixWorld(true);
    logDatumAxisDebug('更新正式基准轴', {
      datumAxisId: this.datumAxisId,
      creationContext: this.creationContext,
      name: this.name,
      originWorld: formatDatumAxisDebugVector(this.originWorld),
      direction: formatDatumAxisDebugVector(this.direction),
      startWorld: formatDatumAxisDebugVector(input.startWorld),
      endWorld: formatDatumAxisDebugVector(input.endWorld),
      axisLength: input.startWorld.distanceTo(input.endWorld),
      position: buildDatumAxisPositionDebug(this),
    });
  }


  /**
   * 获取标签对象。
   *
   * @returns 标签对象。
   */
  public getLabelObject(): CSS2DObject {
    return this.labelObject;
  }

  /**
   * 设置可见性。
   *
   * @param visible 是否可见。
   */
  public setVisible(visible: boolean): void {
    this.visible = visible;
    this.labelObject.visible = visible;
  }

  /**
   * 获取可见性。
   *
   * @returns 是否可见。
   */
  public isVisible(): boolean {
    return this.visible;
  }


  /**
   * 读取基准轴显示状态快照。
   *
   * @returns 显示状态快照。
   */
  public getDisplaySnapshot(): DatumAxisDisplaySnapshot {
    return {
      color: this.displayColor,
      opacity: this.displayOpacity,
    };
  }


  /**
   * 更新名称。
   *
   * @param nextName 新名称。
   */
  public setName(nextName: string): void {
    this.name = nextName;
    if (this.labelObject.element) {
      this.labelObject.element.textContent = nextName;
    }
  }

  /**
   * 释放资源。
   */
  public dispose(): void {
    if (this.geometry instanceof THREE.BufferGeometry) {
      this.geometry.dispose();
    }
    if (this.material instanceof THREE.Material) {
      this.material.dispose();
    }
  }
}
